/**
 * KeywordDetail - Redesigned Keyword Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../components/container';
import { dfKeywordModel, KeywordModel, validKeywordSchema } from '../../context/models/keyword/KeywordModel';
import { slugHelper } from '../../utils/slugHelper';
import ComboBoxProductCategory from '../../components/shared/hoc-product-category/ComboBoxProductCategory';
import { Tag, Info, Hash } from 'lucide-react';

type IKeywordDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: KeywordModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: KeywordModel) => void;
};

const KeywordDetail = (props: IKeywordDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<KeywordModel>(dfKeywordModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<KeywordModel>({
		resolver: yupResolver(validKeywordSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: KeywordModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	const watchChangeName = useWatch({ control, name: 'name', defaultValue: '' });

	useEffect(() => {
		const slug = slugHelper.toSlug(watchChangeName);
		setValue('slug', slug);
		setInitialValues((prev) => ({ ...prev, slug }));
	}, [watchChangeName, setValue]);

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof KeywordModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content
	const categoryTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Danh mục sản phẩm'
				required
				error={errors.catId?.message}
				helpText='Chọn danh mục cha cho từ khóa'
			>
				<ComboBoxProductCategory
					name='catId'
					error={errors.catId?.message}
					onValueChanged={(value) => updateField('catId', value)}
					value={initialValues.catId}
					hasValid={true}
					placeholder='Chọn danh mục...'
				/>
			</EditPageField>
		</div>
	);

	const infoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên từ khóa'
				required
				error={errors.name?.message}
				helpText='Tên hiển thị của từ khóa sản phẩm'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên từ khóa...'
				/>
			</EditPageField>

			<EditPageField
				label='Slug'
				error={errors.slug?.message}
				helpText='Đường dẫn thân thiện cho SEO'
			>
				<TextBox
					name='slug'
					error={errors.slug?.message}
					onValueChanged={(value) => updateField('slug', value)}
					value={initialValues.slug}
					hasValid={true}
				/>
			</EditPageField>

			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'info',
			label: 'Thông tin từ khóa',
			icon: <Info className='h-4 w-4' />,
			content: infoTab,
		},
		{
			id: 'category',
			label: 'Danh mục',
			icon: <Tag className='h-4 w-4' />,
			content: categoryTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới từ khóa sản phẩm' : 'Chỉnh sửa từ khóa sản phẩm'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Từ khóa sản phẩm', href: '/keyword' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default KeywordDetail;
